import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import i18n from 'i18next';
import api from '../config/api';
import CURRENCY_RATES from '../constants/currencyRates.json';

const STORAGE_KEY = 'ipm_user_preferences';

// Defaults are tuned for the South African market (the only geo we support
// today per the May 2026 audit). Users can still override per-account.
const defaultPrefs = {
  language: 'en',
  currency: 'ZAR',
  units: 'sqm',
  /** 'gross' = price is the price (default for SA); 'net' = tax/fees not included */
  priceDisplayMode: 'gross',
};

// Exchange rates: 1 USD = this many units of the currency (e.g. 1 USD = 0.92 EUR). Approximate for display.
/** Convert amount from source currency to target currency (via USD). */
export function convertCurrency(amount, fromCurrency, toCurrency) {
  const from = CURRENCY_RATES[fromCurrency] || 1;
  const to = CURRENCY_RATES[toCurrency] || 1;
  if (!amount || isNaN(amount)) return 0;
  return (Number(amount) / from) * to;
}

const PreferencesContext = createContext(null);

export function PreferencesProvider({ children }) {
  const [prefs, setPrefsState] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const stored = localStorage.getItem(STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : {};
      const serverPrefs = user.preferences || {};
      return {
        ...defaultPrefs,
        ...parsed,
        language: defaultPrefs.language,
        currency: serverPrefs.currency || parsed.currency || defaultPrefs.currency,
        units: serverPrefs.units || parsed.units || defaultPrefs.units,
        priceDisplayMode: serverPrefs.priceDisplayMode || parsed.priceDisplayMode || defaultPrefs.priceDisplayMode,
      };
    } catch (e) { /* ignore */ }
    return defaultPrefs;
  });

  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const serverCurrency = user.preferences?.currency;
      const serverUnits = user.preferences?.units;
      const serverMode = user.preferences?.priceDisplayMode;
      // ZAR / sqm are first-time empty-pref defaults only. A saved USD or sqft
      // choice is real and must not be overwritten.
      if (serverCurrency || serverUnits || serverMode) {
        setPrefsState((p) => ({
          ...p,
          currency: serverCurrency || p.currency,
          units: serverUnits || p.units,
          priceDisplayMode: serverMode || p.priceDisplayMode,
        }));
      }
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (e) { /* ignore */ }
  }, [prefs]);

  const syncTimer = useRef(null);
  const persistToServer = useCallback((updated) => {
    const serverPrefs = { currency: updated.currency, units: updated.units, priceDisplayMode: updated.priceDisplayMode };
    try {
      const existing = JSON.parse(localStorage.getItem('user') || '{}');
      if (existing._id) {
        localStorage.setItem('user', JSON.stringify({
          ...existing,
          preferences: { ...(existing.preferences || {}), ...serverPrefs },
        }));
      }
    } catch (e) { /* ignore */ }
    clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (!user._id) return;
        api.put(`/api/users/${user._id}`, { preferences: serverPrefs }, { skipAuthRedirectOn401: true }).then((res) => {
          const latest = JSON.parse(localStorage.getItem('user') || '{}');
          const fromServer = res.data?.user?.preferences;
          localStorage.setItem('user', JSON.stringify({
            ...latest,
            preferences: { ...(latest.preferences || {}), ...serverPrefs, ...(fromServer || {}) },
          }));
        }).catch(() => {});
      } catch (e) { /* ignore */ }
    }, 800);
  }, []);

  const setLanguage = useCallback((lang) => {
    setPrefsState((p) => ({ ...p, language: lang }));
    i18n.changeLanguage(lang);
  }, []);
  const setCurrency = useCallback((curr) => {
    setPrefsState((p) => {
      const next = { ...p, currency: curr };
      persistToServer(next);
      return next;
    });
  }, [persistToServer]);
  const setUnits = useCallback((u) => {
    setPrefsState((p) => {
      const next = { ...p, units: u };
      persistToServer(next);
      return next;
    });
  }, [persistToServer]);
  const setPriceDisplayMode = useCallback((mode) => {
    setPrefsState((p) => {
      const next = { ...p, priceDisplayMode: mode === 'net' ? 'net' : 'gross' };
      persistToServer(next);
      return next;
    });
  }, [persistToServer]);

  const lang = prefs.language || 'en';
  const currency = prefs.currency || defaultPrefs.currency;
  const units = prefs.units || defaultPrefs.units;
  const priceDisplayMode = prefs.priceDisplayMode || 'gross';

  const getPriceValue = useCallback((priceStr) => {
    if (!priceStr) return 0;
    const str = String(priceStr).toLowerCase().split('|')[0].trim();
    // Rental strings like "ZAR 15000/month" must not treat the "m" in "month" as millions.
    const isMonthlyRent = /\/\s*month\b|\bper\s*month\b|\bmonthly\b|\bpm\b/.test(str);
    let multiplier = 1;
    if (!isMonthlyRent) {
      if (/\d\s*m\b|\d+m\b/.test(str)) multiplier = 1000000;
      else if (/\d\s*k\b|\d+k\b/.test(str)) multiplier = 1000;
    }
    const val = parseFloat(str.replace(/[^0-9.]/g, ''));
    return isNaN(val) ? 0 : val * multiplier;
  }, []);

  /**
   * @param {string|number} priceStrOrNumber
   * @param {{ fromCurrency?: string }} [opts] - Currency the amount is stored in (defaults USD). PropData listings use ZAR in pricing.currency.
   */
  const formatPrice = useCallback((priceStrOrNumber, opts = {}) => {
    if (opts.displayLabel && typeof priceStrOrNumber === 'string' && priceStrOrNumber.trim() === opts.displayLabel) return opts.displayLabel;
    const num = typeof priceStrOrNumber === 'number'
      ? priceStrOrNumber
      : getPriceValue(priceStrOrNumber);
    if (num === 0) return opts.unsetPriceLabel || '-';
    const fromCurrency = opts.fromCurrency || defaultPrefs.currency;
    const converted = convertCurrency(num, fromCurrency, currency);
    const locale = currency === 'ZAR' ? 'en-ZA'
      : lang === 'ar' ? 'ar-AE'
      : lang === 'es' ? 'es-ES'
      : lang === 'fr' ? 'fr-FR'
      : 'en-US';
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return formatter.format(converted);
  }, [currency, lang, getPriceValue]);

  /**
   * @param {{ inputUnit?: 'sqft' | 'sqm', metric?: boolean }} [opts] - Pass inputUnit: 'sqm' when the number is already square metres (PropData import).
   */
  const formatArea = useCallback((value, opts = {}) => {
    const num = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0;
    const inputIsSqm = opts.inputUnit === 'sqm' || opts.metric;
    if (inputIsSqm) {
      if (units === 'sqm') {
        return `${Math.round(num).toLocaleString()} ${i18n.t('common.sqm')}`;
      }
      const sqft = num * 10.764;
      return `${Math.round(sqft).toLocaleString()} ${i18n.t('common.sqft')}`;
    }
    if (units === 'sqm') {
      const sqm = num / 10.764;
      return `${Math.round(sqm).toLocaleString()} ${i18n.t('common.sqm')}`;
    }
    return `${Math.round(num).toLocaleString()} ${i18n.t('common.sqft')}`;
  }, [units]);

  /** Convert amount in source currency to user's preferred currency. */
  const convertToPreferredCurrency = useCallback((amount, fromCurrency) => {
    return convertCurrency(amount, fromCurrency || defaultPrefs.currency, currency);
  }, [currency]);

  const CURRENCY_SYMBOLS = { USD: '$', EUR: '€', GBP: '£', ZAR: 'R', AED: 'AED ', AUD: 'A$' };

  /** Format a number (already in preferred currency) as compact asset value e.g. "R 2.1M", "$ 44.3K". */
  const formatAssetValueCompact = useCallback((valueInPreferredCurrency) => {
    const v = Number(valueInPreferredCurrency) || 0;
    if (v === 0) return '-';
    const cur = currency || defaultPrefs.currency;
    const sym = CURRENCY_SYMBOLS[cur] || `${cur} `;
    const compact = v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}K` : v.toLocaleString();
    return `${sym}${compact}`;
  }, [currency]);

  /** When priceDisplayMode is 'net', return " (ex. tax)" for transparency; otherwise "". */
  const getPriceDisplaySuffix = useCallback(() => (priceDisplayMode === 'net' ? ' (ex. tax)' : ''), [priceDisplayMode]);

  const value = {
    language: lang,
    currency,
    units,
    priceDisplayMode,
    setLanguage,
    setCurrency,
    setUnits,
    setPriceDisplayMode,
    formatPrice,
    formatArea,
    getPriceValue,
    convertToPreferredCurrency,
    formatAssetValueCompact,
    getPriceDisplaySuffix,
  };

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within PreferencesProvider');
  return ctx;
}
